"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
const three_1 = require("three");
const types_1 = require("~/store/types");
const FontLoader_js_1 = require("three/examples/jsm/loaders/FontLoader.js");
const utils_1 = require("./utils");
const utils_3d_1 = require("../../utils/utils.3d");
const BomBalloon_1 = __importDefault(require("./BomBalloon"));
const ObjectContour_1 = __importDefault(require("./ObjectContour"));
const FONT_PATHNAME = '../../../assets/fonts/OpenSans_Regular.typeface.json';
const BALLOON_RADIUS = 0.03;
const RAYS_COUNT = 5;
const BALLOONS_DEPTH = 3;
function shouldUseRayCasting(object) {
    return (0, utils_3d_1.isFastener)(object) || (0, utils_3d_1.isComponent)(object);
}
// return intersection point closest to begin of the segment. If there is no intersection returns null
function getClosestIntersection(segmentBegin, segmentEnd, polygon) {
    let resultingPoint = null;
    let minDistance = Infinity;
    for (const [begin, end] of (0, utils_1.getPolygonSegments)(polygon)) {
        const intersectionPoint = (0, utils_1.getLineSegmentsIntersectionPointXY)(segmentBegin, segmentEnd, begin, end);
        if (intersectionPoint) {
            const distanceToBalloon = intersectionPoint.distanceToSquared(segmentBegin);
            if (distanceToBalloon < minDistance) {
                resultingPoint = intersectionPoint;
                minDistance = distanceToBalloon;
            }
        }
    }
    return resultingPoint;
}
function getMaxByFunc(array, func) {
    let elementWithMaxValue = null;
    let maxValue = -Infinity;
    array.forEach((element) => {
        const funcValue = func(element);
        if (funcValue > maxValue) {
            maxValue = funcValue;
            elementWithMaxValue = element;
        }
    });
    return [elementWithMaxValue, maxValue];
}
class BomBalloonsManagerV2 {
    constructor(viewer) {
        this.viewer = viewer;
        this.scene = viewer.getScene();
        this.balloons = [];
        this.loadDefaultFont();
    }
    reset(camera, bom, inclusions) {
        this.removeBalloonsFromScene();
        this.balloons = [];
        this.camera = camera;
        this.objectContours = this.collectValidBomItems(bom, inclusions);
        this.sceneBBox = (0, utils_1.calculateSceneBoundingBox)(this.scene);
    }
    loadDefaultFont() {
        return __awaiter(this, void 0, void 0, function* () {
            const fontLoader = new FontLoader_js_1.FontLoader();
            this.font = yield fontLoader.loadAsync(FONT_PATHNAME);
        });
    }
    addBalloonsToScene(camera, bom, inclusions) {
        this.reset(camera, bom, inclusions);
        const sceneBoundingSphere = this.sceneBBox.getBoundingSphere(new three_1.Sphere());
        const RADIUS_SCALE = 1.1;
        const distanceToSphereCenter = this.camera.position.distanceTo(sceneBoundingSphere.center);
        const circleRadius = (RADIUS_SCALE * (sceneBoundingSphere.radius * BALLOONS_DEPTH)) / distanceToSphereCenter;
        const circleCenter = new three_1.Vector3(0, 0, -BALLOONS_DEPTH);
        const objectContours = Array.from(this.objectContours.values());
        const balloonTargetPositions = BomBalloonsManagerV2.collectTargetPositions(objectContours);
        const angles = balloonTargetPositions.map((pos) => {
            const fromCenterToPoint = pos.clone().sub(circleCenter);
            let angle = Math.atan2(fromCenterToPoint.y, fromCenterToPoint.x);
            angle = angle > 0 ? angle : Math.PI * 2 + angle;
            return angle;
        });
        const groupCenters = [0, Math.PI];
        const [leftIndices, rightIndices] = BomBalloonsManagerV2.splitIntoGroups(angles, groupCenters);
        leftIndices.sort((a, b) => {
            const sinA = Math.sin(0.25 * angles[a]); // sin(0.25x) is used because this function grows on interval [pi/2, 3pi/2]
            const sinB = Math.sin(0.25 * angles[b]);
            return sinA - sinB;
        });
        rightIndices.sort((a, b) => {
            if (angles[a] > Math.PI && angles[b] < Math.PI) {
                return -1;
            }
            if (angles[a] < Math.PI && angles[b] > Math.PI) {
                return 1;
            }
            return angles[a] - angles[b];
        });
        const groups = BomBalloonsManagerV2.computeCellsForGroups(groupCenters, [rightIndices.length, leftIndices.length], circleRadius);
        const groupedIndices = [rightIndices, leftIndices];
        groups.forEach((group, groupIndex) => {
            group.forEach((position, positionIndex) => {
                this.createBalloon(position, objectContours[groupedIndices[groupIndex][positionIndex]]);
            });
        });
    }
    removeBalloonsFromScene() {
        this.balloons.forEach((balloon) => balloon.removeFromCamera());
    }
    collectValidBomItems(bom, inclusions = {
        EXTRUSION: true,
        COMPONENT: true,
        FASTENER: true,
    }) {
        const validBOMItems = new Map();
        if (!bom.groups) {
            return validBOMItems;
        }
        const objectsMap = new Map();
        const connectionObjects = this.scene.children.filter(utils_3d_1.isConnection);
        let lineItemNumber = 1;
        bom.groups.forEach((group) => {
            const groupName = group.name.toLowerCase();
            const isExtrusionsGroup = groupName === 'extrusions';
            const isComponentsGroup = groupName === 'components';
            const isGroupIncluded = (isExtrusionsGroup && (inclusions === null || inclusions === void 0 ? void 0 : inclusions.EXTRUSION)) || (isComponentsGroup && (inclusions === null || inclusions === void 0 ? void 0 : inclusions.COMPONENT));
            group.items.forEach((item) => {
                var _a, _b;
                const itemNumber = lineItemNumber;
                lineItemNumber += 1;
                if (!isExtrusionsGroup && !isComponentsGroup) {
                    return;
                }
                const parts = [];
                if (((_a = item.componentType) === null || _a === void 0 ? void 0 : _a.name) === types_1.COMPONENT_TYPE.FASTENER) {
                    const fasteners = connectionObjects.flatMap((connectionObject) => connectionObject.getFasteners().filter((fastener) => fastener.partDetails.itemNumber === item.item_number));
                    if (inclusions === null || inclusions === void 0 ? void 0 : inclusions.FASTENER) {
                        objectsMap.set(itemNumber, fasteners.map((fastener) => new ObjectContour_1.default(fastener, this.camera, itemNumber, BALLOONS_DEPTH)));
                    }
                }
                else {
                    (_b = item.uuid) === null || _b === void 0 ? void 0 : _b.forEach((uuid) => {
                        const part = this.viewer.getObjectById(uuid);
                        // likely uuid belongs to fastener
                        if (part && (0, utils_3d_1.isBaseObject3D)(part)) {
                            parts.push(new ObjectContour_1.default(part, this.camera, itemNumber, BALLOONS_DEPTH));
                        }
                    });
                    if (parts.length && isGroupIncluded) {
                        objectsMap.set(itemNumber, parts);
                    }
                }
            });
        });
        const allContours = Array.from(objectsMap.values()).flat();
        objectsMap.forEach((contours) => {
            contours.forEach((contour) => {
                const object = contour.getObject();
                if (shouldUseRayCasting(object)) {
                    return;
                }
                const otherContours = allContours.filter((c) => c !== contour && !(0, utils_3d_1.isFastener)(c.getObject()));
                contour.computeVisibleParts(otherContours);
            });
        });
        objectsMap.forEach((contours, idx) => {
            if (contours.length && shouldUseRayCasting(contours[0].getObject())) {
                let objectWithMaxVisiblePoints = null;
                let maxVisiblePoints = [];
                for (const contour of contours) {
                    const otherContours = allContours.filter((c) => c !== contour);
                    const [isVisible, points] = this.testObjectVisibility(contour, otherContours);
                    if (isVisible && points.length > maxVisiblePoints.length) {
                        objectWithMaxVisiblePoints = contour;
                        maxVisiblePoints = points;
                    }
                }
                if (objectWithMaxVisiblePoints && maxVisiblePoints.length !== 0) {
                    if ((0, utils_3d_1.isComponent)(objectWithMaxVisiblePoints.getObject())) {
                        let furthestPoint = maxVisiblePoints[0];
                        let distanceToFurthestPointSq = -Infinity;
                        maxVisiblePoints.forEach((point) => {
                            const distanceToOriginSq = point.x * point.x + point.y * point.y;
                            if (distanceToOriginSq > distanceToFurthestPointSq) {
                                furthestPoint = point;
                                distanceToFurthestPointSq = distanceToOriginSq;
                            }
                        });
                        objectWithMaxVisiblePoints.setCenter(furthestPoint);
                    }
                    else {
                        objectWithMaxVisiblePoints.setCenter((0, utils_1.calculateCentroid)(maxVisiblePoints));
                    }
                    validBOMItems.set(idx, objectWithMaxVisiblePoints);
                }
                return;
            }
            let partWithMaxVisibility = null;
            let maxAreaPercent = -Infinity;
            for (const contour of contours) {
                const areaPercent = contour.getVisibleAreaPercent();
                if (areaPercent > maxAreaPercent) {
                    maxAreaPercent = areaPercent;
                    partWithMaxVisibility = contour;
                }
            }
            if (partWithMaxVisibility && !(0, utils_3d_1.areEqual)(maxAreaPercent, 0)) {
                validBOMItems.set(idx, partWithMaxVisibility);
            }
        });
        return validBOMItems;
    }
    static collectTargetPositions(objectContours) {
        return objectContours.map((objectContour) => {
            const object = objectContour.getObject();
            const isObjectPanel = (0, utils_3d_1.isPanel)(object);
            const isObjectFastener = (0, utils_3d_1.isFastener)(object);
            const isObjectComponent = (0, utils_3d_1.isComponent)(object);
            let point = null;
            if (isObjectPanel) {
                point = objectContour.isPointVisible(objectContour.getCenter())
                    ? objectContour.getCenter()
                    : (0, utils_1.calculateCentroid)(objectContour.getLargestVisiblePart());
            }
            else if (isObjectFastener) {
                point = objectContour.getCenter();
            }
            else if (isObjectComponent) {
                point = objectContour.getCenter();
            }
            else {
                point = objectContour.getCenter();
            }
            return point;
        });
    }
    static splitIntoGroups(angles, groupCenters) {
        const elementsInGroup = Math.floor(angles.length / groupCenters.length);
        const additionalElements = angles.length % groupCenters.length;
        const sortedIndices = angles
            .map((_, index) => index)
            .sort((a, b) => {
            const sinA = Math.sin(0.5 * angles[a]);
            const sinB = Math.sin(0.5 * angles[b]);
            return sinB - sinA;
        });
        const left = sortedIndices.slice(0, elementsInGroup + additionalElements);
        const right = sortedIndices.slice(elementsInGroup + additionalElements);
        return [left, right];
    }
    createBalloon(balloonPosition, contour) {
        const object = contour.getObject();
        const isObjectPanel = (0, utils_3d_1.isPanel)(object);
        const isObjectFastener = (0, utils_3d_1.isFastener)(object);
        const isObjectComponent = (0, utils_3d_1.isComponent)(object);
        let point = null;
        if (isObjectPanel) {
            point = contour.isPointVisible(contour.getCenter())
                ? contour.getCenter()
                : (0, utils_1.calculateCentroid)(contour.getLargestVisiblePart());
        }
        else if (isObjectFastener) {
            point = contour.getCenter();
        }
        else if (isObjectComponent) {
            point = contour.getCenter();
        }
        else {
            point = BomBalloonsManagerV2.projectOnContour(balloonPosition, contour);
        }
        if (!point) {
            return;
        }
        const balloon = new BomBalloon_1.default(balloonPosition, point, contour.getIndex(), this.font, BALLOON_RADIUS);
        balloon.addToCamera(this.camera);
        this.balloons.push(balloon);
    }
    static computeCellsForGroups(groupCenters, groupCounts, radius) {
        if (groupCenters.length < 2) {
            return [];
        }
        const delta = 0.5 * (groupCenters[1] - groupCenters[0]);
        const groupBounds = groupCenters.map((groupCenter) => [groupCenter - delta, groupCenter + delta]);
        const groups = new Array(groupCenters.length);
        for (let i = 0; i < groups.length; i += 1) {
            groups[i] = [];
        }
        groupBounds.forEach(([begin, end], groupIndex) => {
            const step = (end - begin) / groupCounts[groupIndex];
            let angle = begin + 0.5 * step;
            for (let i = 0; i < groupCounts[groupIndex]; i += 1) {
                const x = radius * Math.cos(angle);
                const y = radius * Math.sin(angle);
                groups[groupIndex].push(new three_1.Vector3(x, y, -BALLOONS_DEPTH));
                angle += step;
            }
        });
        return groups;
    }
    static projectOnContour(balloonPosition, contour) {
        const target = contour.getCenter();
        let resultingPoint = null;
        let minDistance = Infinity;
        for (const part of contour.getVisibleParts()) {
            for (const [begin, end] of (0, utils_1.getPolygonSegments)(part)) {
                const intersectionPoint = (0, utils_1.getLineSegmentsIntersectionPointXY)(balloonPosition, target, begin, end);
                if (intersectionPoint) {
                    const distanceToBalloon = intersectionPoint.distanceToSquared(balloonPosition);
                    if (distanceToBalloon < minDistance) {
                        resultingPoint = intersectionPoint;
                        minDistance = distanceToBalloon;
                    }
                }
            }
        }
        if (resultingPoint) {
            resultingPoint.add(target.clone().sub(resultingPoint).normalize().multiplyScalar(5e-3));
            return resultingPoint;
        }
        // center is covered by another object
        const [part, area] = getMaxByFunc(contour.getVisibleParts(), three_1.ShapeUtils.area);
        if (!(0, utils_3d_1.areEqual)(area, 0) && part) {
            const centroid = (0, utils_1.calculateCentroid)(part);
            const point = getClosestIntersection(balloonPosition, centroid, part);
            return point === null || point === void 0 ? void 0 : point.add(centroid.clone().sub(point).normalize().multiplyScalar(5e-3));
        }
        return null;
    }
    testObjectVisibility(fastenerContour, surroundingObjects) {
        const box = new three_1.Box2().setFromPoints((0, utils_1.toVector2Array)(fastenerContour.getHull()));
        const tempVector = new three_1.Vector2();
        const filteredObjects = surroundingObjects.filter((contour) => {
            const objectHull = fastenerContour.getHull();
            const neighborHull = contour.getHull();
            return (neighborHull.some((point) => box.containsPoint(tempVector.set(point.x, point.y))) &&
                ((0, utils_1.getPolygonsIntersections)(objectHull, neighborHull).length > 0 ||
                    (0, utils_1.isPolygonInsidePolygon)(objectHull, neighborHull) ||
                    (0, utils_1.isPolygonInsidePolygon)(neighborHull, objectHull)));
        });
        const hStep = (box.max.x - box.min.x) / RAYS_COUNT;
        const vStep = (box.max.y - box.min.y) / RAYS_COUNT;
        let py = box.min.y + 0.5 * vStep;
        const fastener = fastenerContour.getObject();
        const objects = [fastener.mesh, ...filteredObjects.map((obj) => obj.getObject().mesh)];
        const raycaster = new three_1.Raycaster();
        const intersections = new Array(RAYS_COUNT);
        for (let i = 0; i < RAYS_COUNT; i += 1) {
            intersections[i] = new Array(RAYS_COUNT);
        }
        let intersectionFound = false;
        for (let i = 0; i < RAYS_COUNT; i += 1) {
            let px = box.min.x + 0.5 * hStep;
            for (let j = 0; j < RAYS_COUNT; j += 1) {
                const currPoint = new three_1.Vector3(px, py, -BALLOONS_DEPTH);
                if (!(0, utils_1.isPointInsidePolygon)(currPoint, fastenerContour.getHull())) {
                    continue;
                }
                const pointInWorldSpace = this.camera.localToWorld(currPoint.clone());
                const rayDirection = pointInWorldSpace.sub(this.camera.position).normalize();
                raycaster.set(this.camera.position, rayDirection);
                const rayIntersections = raycaster.intersectObjects(objects, false);
                if (rayIntersections.length) {
                    if (rayIntersections[0].object === fastener.mesh) {
                        [intersections[i][j]] = rayIntersections;
                        intersectionFound = true;
                    }
                }
                px += hStep;
            }
            py += vStep;
        }
        const possiblePoints = intersections
            .flat()
            .map((intersection) => (0, utils_1.projectOnCameraPlane)(this.camera.worldToLocal(intersection.point.clone()), BALLOONS_DEPTH));
        return [intersectionFound, possiblePoints];
    }
}
exports.default = BomBalloonsManagerV2;
